/**
 * Field Detection Engine
 * 
 * Comprehensive field detection engine that scans DOM for data-name attributes,
 * identifies field types, and establishes the foundation for format application.
 * Provides graceful degradation when attributes are missing.
 */

import { 
  FieldType, 
  FieldDetectionPatterns, 
  FieldDetectionResult,
  DetectionConfidence 
} from './detection-patterns';

import { 
  FieldRegistry, 
  FieldInfo, 
  getFieldRegistry 
} from './field-registry';

import { initializeAddressAutocomplete, ADDRESS_CONFIG } from '../address';

/**
 * Configuration options for the field detector
 */
export interface FieldDetectorConfig {
  /** Enable debug logging */
  debugMode?: boolean;
  /** Minimum confidence threshold for field detection */
  minConfidence?: number;
  /** Whether to scan for fields without data-name attributes */
  enableFallbackDetection?: boolean;
  /** Whether to monitor for dynamically added fields */
  enableDynamicDetection?: boolean;
  /** Custom selectors for field scanning */
  customSelectors?: string[];
  /** Whether to gracefully degrade on errors */
  gracefulDegradation?: boolean;
}

/**
 * Results from a field detection scan
 */
export interface DetectionScanResult {
  /** Total fields scanned */
  fieldsScanned: number;
  /** Fields successfully detected and registered */
  fieldsDetected: number;
  /** Fields detected by type */
  detectionsByType: Record<FieldType, number>;
  /** Fields with high confidence detection */
  highConfidenceDetections: number;
  /** Scan duration in milliseconds */
  scanDuration: number;
  /** Any errors that occurred during scanning */
  errors: string[];
  /** Scan timestamp */
  timestamp: Date;
}

/**
 * Main Field Detection Engine
 * 
 * Scans DOM for form fields with data-name attributes and identifies their types
 * for formatting application. Provides comprehensive fallback detection and
 * graceful error handling.
 */
export class FieldDetector {
  private config: Required<FieldDetectorConfig>;
  private registry: FieldRegistry;
  private scanCount: number = 0;

  /**
   * Default configuration
   */
  private static readonly DEFAULT_CONFIG: Required<FieldDetectorConfig> = {
    debugMode: false,
    minConfidence: 0.3,
    enableFallbackDetection: true,
    enableDynamicDetection: true,
    customSelectors: [],
    gracefulDegradation: true
  };

  constructor(config: FieldDetectorConfig = {}) {
    this.config = { ...FieldDetector.DEFAULT_CONFIG, ...config };
    this.registry = getFieldRegistry(this.config.debugMode);
  }

  /**
   * Scan the entire document for form fields
   * @returns Detection scan results
   */
  public scanDocument(): DetectionScanResult {
    const startTime = Date.now();
    const scanId = ++this.scanCount;
    
    this.log(`Starting document scan #${scanId}`);

    const result: DetectionScanResult = {
      fieldsScanned: 0,
      fieldsDetected: 0,
      detectionsByType: {
        [FieldType.PHONE]: 0,
        [FieldType.ZIP]: 0,
        [FieldType.DATE_OF_BIRTH]: 0,
        [FieldType.CURRENCY]: 0,
        [FieldType.SSN]: 0,
        [FieldType.UNKNOWN]: 0
      },
      highConfidenceDetections: 0,
      scanDuration: 0,
      errors: [],
      timestamp: new Date()
    };

    try {
      // Primary scan: Look for fields with data-name attributes
      const fieldsWithDataName = this.findFieldsWithDataName();
      result.fieldsScanned += fieldsWithDataName.length;

      for (const element of fieldsWithDataName) {
        try {
          const fieldInfo = this.analyzeField(element);
          if (fieldInfo && fieldInfo.detection.confidence >= this.config.minConfidence) {
            this.registry.registerField(fieldInfo);
            result.fieldsDetected++;
            result.detectionsByType[fieldInfo.fieldType]++;
            
            if (fieldInfo.detection.confidence >= 0.8) {
              result.highConfidenceDetections++;
            }
          }
        } catch (error) {
          const errorMsg = `Error analyzing field with data-name: ${error}`;
          result.errors.push(errorMsg);
          this.log(errorMsg, 'error');
          
          if (!this.config.gracefulDegradation) {
            throw new Error(errorMsg);
          }
        }
      }

      // Fallback scan: Look for fields without data-name attributes
      if (this.config.enableFallbackDetection) {
        const fallbackFields = this.findFieldsForFallbackDetection();
        result.fieldsScanned += fallbackFields.length;

        for (const element of fallbackFields) {
          try {
            const fieldInfo = this.analyzeField(element);
            if (fieldInfo && fieldInfo.detection.confidence >= this.config.minConfidence) {
              this.registry.registerField(fieldInfo);
              result.fieldsDetected++;
              result.detectionsByType[fieldInfo.fieldType]++;
              
              if (fieldInfo.detection.confidence >= 0.8) {
                result.highConfidenceDetections++;
              }
            }
          } catch (error) {
            const errorMsg = `Error analyzing fallback field: ${error}`;
            result.errors.push(errorMsg);
            this.log(errorMsg, 'error');
            
            if (!this.config.gracefulDegradation) {
              throw new Error(errorMsg);
            }
          }
        }
      }

      // Start dynamic detection if enabled
      if (this.config.enableDynamicDetection) {
        this.startDynamicDetection();
      }

      result.scanDuration = Date.now() - startTime;
      this.registry.setLastScanTime(result.timestamp);
      
      this.log(`Scan #${scanId} complete: ${result.fieldsDetected}/${result.fieldsScanned} fields detected in ${result.scanDuration}ms`);

    } catch (error) {
      const errorMsg = `Critical error during document scan: ${error}`;
      result.errors.push(errorMsg);
      result.scanDuration = Date.now() - startTime;
      
      this.log(errorMsg, 'error');
      
      if (!this.config.gracefulDegradation) {
        throw new Error(errorMsg);
      }
    }

    return result;
  }

  /**
   * Analyze a specific form field element
   * @param element The form field element to analyze
   * @returns Field information or null if not detectable
   */
  public analyzeField(element: HTMLInputElement | HTMLTextAreaElement): FieldInfo | null {
    try {
      if (!this.isValidFormField(element)) {
        return null;
      }

      // Extract attributes for detection
      const dataName = element.getAttribute('data-name');
      const inputType = element.getAttribute('type');
      const placeholder = element.getAttribute('placeholder');
      const fieldName = element.getAttribute('name') || element.getAttribute('id');

      // Determine field type using comprehensive detection
      const detection = FieldDetectionPatterns.determineFieldType(
        dataName || undefined,
        inputType || undefined,
        placeholder || undefined,
        fieldName || undefined
      );

      // Create field info object
      const fieldInfo: FieldInfo = {
        element,
        dataName,
        fieldType: detection.fieldType,
        detection: {
          fieldType: detection.fieldType,
          confidence: detection.confidence,
          method: detection.method,
          requiresFormatting: FieldDetectionPatterns.requiresFormatting(detection.fieldType),
          expectedFormat: FieldDetectionPatterns.getExpectedFormatPattern(detection.fieldType) || undefined
        },
        currentValue: element.value || '',
        isActive: document.activeElement === element,
        detectedAt: new Date()
      };

      this.log(`Analyzed field: type=${detection.fieldType}, confidence=${detection.confidence}, method=${detection.method}`);

      // Initialize address autocomplete for address street fields
      if (this.isAddressStreetField(dataName, fieldName, placeholder)) {
        this.initializeAddressAutocompleteForField(element as HTMLInputElement);
      }

      return fieldInfo;

    } catch (error) {
      this.log(`Error analyzing field: ${error}`, 'error');
      
      if (!this.config.gracefulDegradation) {
        throw error;
      }
      
      return null;
    }
  }

  /**
   * Get the field registry instance
   * @returns Field registry
   */
  public getRegistry(): FieldRegistry {
    return this.registry;
  }

  /**
   * Start monitoring for dynamically added fields
   */
  public startDynamicDetection(): void {
    if (this.config.enableDynamicDetection) {
      this.registry.startDynamicDetection((element) => {
        // Analyze and register new fields
        const fieldInfo = this.analyzeField(element as any);
        if (fieldInfo && fieldInfo.detection.confidence >= this.config.minConfidence) {
          this.registry.registerField(fieldInfo);
          this.log(`Registered dynamic field: ${fieldInfo.fieldType}`);
        }
      });
    }
  }

  /**
   * Stop dynamic detection
   */
  public stopDynamicDetection(): void {
    this.registry.stopDynamicDetection();
  }

  /**
   * Cleanup and reset the detector
   */
  public cleanup(): void {
    this.registry.clear();
    this.scanCount = 0;
    this.log('Field detector cleanup complete');
  }

  /**
   * Find all form fields with data-name attributes
   * @returns Array of form field elements
   */
  private findFieldsWithDataName(): (HTMLInputElement | HTMLTextAreaElement)[] {
    const selectors = [
      'input[data-name]',
      'textarea[data-name]',
      ...this.config.customSelectors
    ];

    const elements: (HTMLInputElement | HTMLTextAreaElement)[] = [];

    for (const selector of selectors) {
      try {
        const found = document.querySelectorAll(selector);
        found.forEach(element => {
          if (this.isValidFormField(element)) {
            elements.push(element as any);
          }
        });
      } catch (error) {
        this.log(`Error with selector "${selector}": ${error}`, 'error');
      }
    }

    // Remove duplicates
    return Array.from(new Set(elements));
  }

  /**
   * Find form fields for fallback detection (without data-name attributes)
   * @returns Array of form field elements
   */
  private findFieldsForFallbackDetection(): (HTMLInputElement | HTMLTextAreaElement)[] {
    const selectors = [
      'input:not([data-name])',
      'textarea:not([data-name])'
    ];

    const elements: (HTMLInputElement | HTMLTextAreaElement)[] = [];

    for (const selector of selectors) {
      try {
        const found = document.querySelectorAll(selector);
        found.forEach(element => {
          if (this.isValidFormField(element)) {
            elements.push(element as any);
          }
        });
      } catch (error) {
        this.log(`Error with fallback selector "${selector}": ${error}`, 'error');
      }
    }

    return Array.from(new Set(elements));
  }

  /**
   * Check if element is a valid form field for detection
   * @param element Element to check
   * @returns Boolean indicating validity
   */
  private isValidFormField(element: any): element is (HTMLInputElement | HTMLTextAreaElement) {
    if (!element || !element.tagName) {
      return false;
    }

    const tagName = element.tagName.toLowerCase();
    // Exclude select elements from formatting - they don't need text formatting
    const validTags = ['input', 'textarea'];

    if (!validTags.includes(tagName)) {
      return false;
    }

    // Skip hidden fields and buttons
    if (tagName === 'input') {
      const inputType = (element.type || '').toLowerCase();
      const skipTypes = ['hidden', 'button', 'submit', 'reset', 'image'];
      if (skipTypes.includes(inputType)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if field is an address street field that should get autocomplete
   */
  private isAddressStreetField(dataName?: string | null, fieldName?: string | null, placeholder?: string | null): boolean {
    const indicators = [
      dataName?.toLowerCase(),
      fieldName?.toLowerCase(),
      placeholder?.toLowerCase()
    ].filter(Boolean);

    // Only match fields specifically for street address, not city/state/zip
    return indicators.some(indicator => 
      indicator === 'address_street' || 
      indicator === 'street' ||
      indicator === 'address-street' ||
      (indicator?.includes('address') && indicator?.includes('street')) ||
      (indicator?.includes('address') && indicator?.includes('line') && !indicator?.includes('city') && !indicator?.includes('state') && !indicator?.includes('zip'))
    );
  }

  /**
   * Initialize address autocomplete for a specific field
   */
  private initializeAddressAutocompleteForField(element: HTMLInputElement): void {
    try {
      const autocomplete = initializeAddressAutocomplete(element, ADDRESS_CONFIG);
      this.log(`Address autocomplete initialized for field: ${element.name || element.id}`);
    } catch (error) {
      this.log(`Failed to initialize address autocomplete: ${error}`, 'warn');
    }
  }

  /**
   * Log messages with debug mode check
   * @param message The message to log
   * @param level Log level
   */
  private log(message: string, level: 'log' | 'warn' | 'error' = 'log'): void {
    if (this.config.debugMode) {
      console[level](`[FieldDetector] ${message}`);
    }
  }
}

/**
 * Singleton detector instance
 */
let detectorInstance: FieldDetector | null = null;

/**
 * Get or create the global field detector instance
 * @param config Configuration options
 * @returns The field detector instance
 */
export function getFieldDetector(config?: FieldDetectorConfig): FieldDetector {
  if (!detectorInstance) {
    detectorInstance = new FieldDetector(config);
  }
  return detectorInstance;
}

/**
 * Reset the global detector instance (primarily for testing)
 */
export function resetFieldDetector(): void {
  if (detectorInstance) {
    detectorInstance.cleanup();
  }
  detectorInstance = null;
}

/**
 * Quick scan utility function
 * Performs a document scan with default configuration
 * @returns Detection scan results
 */
export function quickScan(): DetectionScanResult {
  const detector = getFieldDetector();
  return detector.scanDocument();
}